import { Link } from "react-router-dom";
import { Check, MessageCircle, Mail, Phone, ArrowRight, FileText, ShieldCheck, Calculator, ClipboardList, Archive, Sparkles, Award, AlertTriangle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import SEO, { ORG_JSONLD, breadcrumb, serviceJsonLd } from "../components/SEO";

const WHATSAPP_MSG = WA_MSG.postsubasta;
const MAIL_URL = "mailto:fmsubastasinvestment@gmail.com?subject=Gestión%20Post-Subasta";

const INCLUDES = [
  {
    icon: FileText, title: "Asesoramiento del pago judicial",
    desc: "Una vez el juzgado emite el decreto de adjudicación y la carta de pago judicial, te guiamos en el procedimiento para realizar correctamente la transferencia. Pago sin errores ni incidencias.",
  },
  {
    icon: ShieldCheck, title: "Mandamiento de cancelación de cargas",
    desc: "Solicitamos al juzgado el documento judicial que permite eliminar del Registro las cargas posteriores a la ejecución (hipotecas posteriores, embargos, anotaciones). Para que la propiedad quede registralmente limpia.",
  },
  {
    icon: Calculator, title: "Liquidación del ITP",
    desc: "Calculamos y presentamos el Impuesto de Transmisiones Patrimoniales dentro del plazo legal de 30 días hábiles según la Comunidad Autónoma. Evitamos recargos, sanciones y errores administrativos.",
  },
  {
    icon: ClipboardList, title: "Regularización de deudas NO registrales",
    desc: "Te acompañamos con asesoramiento y paso a paso para regularizar deudas con la Comunidad de propietarios, IBI, tasas municipales o basura. Por la normativa de protección de datos, determinadas gestiones solo puede realizarlas el titular, pero te indicamos exactamente qué solicitar, cómo y en qué orden.",
  },
  {
    icon: FileText, title: "Inscripción en el Registro de la Propiedad",
    desc: "Presentamos toda la documentación: testimonio del decreto de adjudicación, mandamientos judiciales y justificantes de liquidación. Obtienes tu título de propiedad registral definitivo.",
  },
  {
    icon: Archive, title: "Dosier completo del expediente",
    desc: "Cuando el proceso finaliza recibes un dosier digital con justificante del pago judicial, liquidación del impuesto, nota simple actualizada, documentación registral e información fiscal futura del inmueble.",
  },
];

const FEES = [
  { range: "Hasta 200.000 € de adjudicación", percent: "1,5 % + IVA" },
  { range: "Entre 200.001 € y 299.000 €", percent: "1,25 % + IVA" },
  { range: "A partir de 300.000 €", percent: "1 % + IVA" },
];

export default function PostSubasta() {
  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Gestión Completa Post-Subasta · Tramitación integral"
        description="Tras la adjudicación nos encargamos de todo: ITP, inscripción registral, plusvalía, gestiones con el juzgado, posesión y defensa jurídica. Honorarios desde 1 % (mín. 800 € + IVA)."
        keywords="gestión post subasta, tramitación adjudicación subasta, inscripción registral subasta, plusvalía municipal subasta, posesión inmueble subastado"
        path="/gestion-postsubasta"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }, { name: "Gestión Post-Subasta", path: "/gestion-postsubasta" }]),
          serviceJsonLd({
            name: "Gestión Completa Post-Subasta",
            description: "Tramitación integral tras la adjudicación de un inmueble en subasta: ITP, registro, plusvalía, juzgado, posesión y asistencia jurídica.",
            path: "/gestion-postsubasta",
          }),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] px-3 py-1 rounded-sm text-[10px] tracking-[0.25em] uppercase font-bold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Has ganado la subasta · ¿Y ahora qué?
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              Gestión <span className="text-[#E0651F]">Post-Subasta</span>.<br />Hasta tener la propiedad a tu nombre.
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed max-w-2xl mb-3 font-display font-medium">
              Enhorabuena por la adjudicación. Ahora empieza la fase que muchos desconocen.
            </p>
            <p className="text-sm text-stone-400 leading-relaxed max-w-2xl mb-8">
              Transferencias judiciales, liquidación de impuestos, mandamientos, cancelación de cargas, inscripción registral, deudas del inmueble, comunicación con administraciones. <strong className="text-white">Un error o un retraso aquí puede generar complicaciones o costes innecesarios.</strong> Te guiamos paso a paso hasta que el inmueble queda correctamente inscrito a tu nombre.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWhatsApp(WHATSAPP_MSG); }}
                className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> Estudiar mi caso
              </a>
              <a href={MAIL_URL} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-[#2d1f1a] p-7 rounded-sm border-l-4 border-[#E0651F] shadow-2xl">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Honorarios</div>
              <h3 className="font-display text-xl font-black tracking-tight mb-5 leading-tight">Sobre el importe de adjudicación.</h3>
              <ul className="space-y-3 text-sm mb-5">
                {FEES.map((f, i) => (
                  <li key={i} className="flex justify-between gap-3 border-b border-stone-200 pb-2">
                    <span className="text-stone-700">{f.range}</span>
                    <span className="font-mono font-bold text-[#2d1f1a]">{f.percent}</span>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-stone-600 leading-relaxed space-y-1">
                <div className="flex justify-between"><span>Honorarios mínimos</span><span className="font-mono font-bold">800 € + IVA</span></div>
                <div className="flex justify-between"><span>Honorarios máximos</span><span className="font-mono font-bold">7.000 € + IVA</span></div>
                <div className="flex justify-between"><span>Provisión Registro Propiedad</span><span className="font-mono font-bold">1.000 €</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">Qué incluye exactamente</div>
          <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-4 leading-tight">El paso final que garantiza tu inversión.</h2>
          <p className="text-base text-stone-600 leading-relaxed">
            Acompañamiento completo durante todo el proceso posterior a la adjudicación, hasta que el inmueble queda correctamente inscrito a tu nombre.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
          {INCLUDES.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2d1f1a] flex items-center justify-center rounded-sm">
                  <Icon className="w-5 h-5 text-[#E0651F]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E0651F] mb-1">{String(i + 1).padStart(2, "0")} · Incluido</div>
                  <h3 className="font-display font-black text-lg text-[#2d1f1a] tracking-tight mb-2 leading-tight">{it.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{it.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROVISION NOTE */}
      <section className="bg-stone-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border-l-4 border-[#E0651F] p-10 rounded-sm shadow-sm">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-7 h-7 text-[#E0651F] flex-shrink-0 mt-1" />
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Provisión de fondos</div>
                <h2 className="font-display font-black text-2xl text-[#2d1f1a] tracking-tight mb-4 leading-tight">1.000 € reembolsables.</h2>
                <p className="text-sm text-stone-700 leading-relaxed">
                  El servicio incluye una <strong>provisión de fondos de 1.000 €</strong> destinada a los gastos del Registro de la Propiedad. <strong>Cualquier importe sobrante se devuelve al cliente</strong> una vez finalizado el proceso.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-3">¿Por qué delegar la gestión?</div>
        <h2 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-6 max-w-3xl leading-tight">
          No es un trámite administrativo. Es <span className="text-[#E0651F]">el paso final</span> que garantiza tu inversión.
        </h2>
        <p className="text-stone-600 text-base leading-relaxed max-w-3xl mb-10">
          +12 años especializados en subastas judiciales y administrativas, con un equipo formado por abogados, procuradores y especialistas fiscales bajo un mismo techo.
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            "Gestión fiscal dentro del plazo legal",
            "Tramitación registral del inmueble",
            "Cancelación de cargas judiciales",
            "Coordinación con juzgados y administraciones",
            "Seguimiento completo del expediente",
            "Equipo jurídico y fiscal propio",
          ].map((b, i) => (
            <div key={i} className="bg-white border border-stone-200 p-5 rounded-sm flex items-start gap-3">
              <Check className="w-5 h-5 text-[#E0651F] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-stone-700">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-[#2d1f1a] text-white py-16">
        <div className="max-w-3xl mx-auto px-6">
          <Award className="w-10 h-10 text-[#E0651F] mb-5" />
          <p className="font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight mb-5">
            "Fernando Marcos se encargó de todo: pago al juzgado, ITP y registro. Me limitaron los costes y en seis semanas tenía la vivienda inscrita a mi nombre."
          </p>
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E0651F]">— Sergio A. · Reseña en Google · 29/04/2025</div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2d1f1a] mb-5 leading-tight">
            ¿Qué tienes que hacer ahora?
          </h2>
          <p className="text-base text-stone-600 mb-8 max-w-xl mx-auto leading-relaxed">
            Muy sencillo. Contacta y estudiamos tu caso. Te guiamos paso a paso para que tu inmueble quede correctamente inscrito, regularizado y sin incidencias.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={buildWaUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); openWhatsApp(WHATSAPP_MSG); }}
              className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
              data-testid="footer-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href="tel:+34695536486" className="inline-flex items-center gap-2 bg-white hover:bg-[#2d1f1a] hover:text-white text-[#2d1f1a] border border-stone-300 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              <Phone className="w-4 h-4" /> +34 695 536 486
            </a>
            <a href={MAIL_URL} className="inline-flex items-center gap-2 bg-white hover:bg-[#2d1f1a] hover:text-white text-[#2d1f1a] border border-stone-300 px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              <Mail className="w-4 h-4" /> Email
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
